import Cookies from 'js-cookie';
import { NI_C_COOKIES, SERVER_PURPOSE_LIMITATION_NAMES, SQ_COOKIES, } from '@nike/privacy-shared-logic';
import { checkIsSubDomain, checkIs13MonthRePromptNeeded, checkRetrievingLimitationGuestFromStore, fetchPurposeLimitationsGuest, fetchPurposeLimitationsMember, getConsentSelectionsForCookie, getCookiePurposeLimitationsValuesToSet, getCookiesPurposeLimitationsEnumKeys, getLevelsFromLimitations, getServerPurposeLimitationEnumKey, getSqCookieValueToSet, setNiCCookie, setSqCookie, checkIsSqCookieDeprecatedByLocation, transformLimitations, } from '../../services/purposeLimitations';
import { REQUIRED_PURPOSE_LIMITATION_PROMPT_LIMIT_LEVEL } from '../constants';
const mergeOldGuestLimitationsWithNewVersion = (oldPurposeLimitations, newPurposeLimitations) => newPurposeLimitations.map(purposeLimitation => {
    const limitationInOldStore = oldPurposeLimitations.find(limitation => limitation.category === purposeLimitation.category && !limitation.isDefault);
    if (limitationInOldStore) {
        return {
            ...purposeLimitation,
            isDefault: false,
            isEnabled: limitationInOldStore.isEnabled,
            userLicenses: purposeLimitation.userLicenses.map(userLicense => ({
                ...userLicense,
                isDefault: false,
                isEnabled: limitationInOldStore.isEnabled,
            })),
        };
    }
    return purposeLimitation;
});
const mergeSubdomainGuestLimitationsFromCookie = (subdomainPurposeLimitations, oldNiCCookies) => subdomainPurposeLimitations.map(subdomainPurposeLimitation => {
    const subdomainLimitationKey = getServerPurposeLimitationEnumKey(subdomainPurposeLimitation);
    if (!subdomainLimitationKey)
        return subdomainPurposeLimitation;
    const subdomainLimitationShortKey = getCookiesPurposeLimitationsEnumKeys(subdomainLimitationKey);
    if (!subdomainLimitationShortKey)
        return subdomainPurposeLimitation;
    const listOfNiCCookies = oldNiCCookies.split('|');
    const mapOfNiCCookies = listOfNiCCookies.reduce((acc, nextCookieValue) => {
        const splittedItems = nextCookieValue.split('=');
        return { ...acc, [splittedItems[0]]: splittedItems[1] };
    }, {});
    const cookiesHasSubdomainKey = subdomainLimitationShortKey in mapOfNiCCookies;
    const subdomainCookieConsent = getConsentSelectionsForCookie(subdomainPurposeLimitation);
    const isConsentTheSameWithCookie = mapOfNiCCookies[subdomainLimitationShortKey] === '1';
    return cookiesHasSubdomainKey &&
        mapOfNiCCookies[subdomainLimitationShortKey] !== subdomainCookieConsent
        ? {
            ...subdomainPurposeLimitation,
            isEnabled: isConsentTheSameWithCookie,
            userLicenses: subdomainPurposeLimitation.userLicenses.map(userLicense => ({
                ...userLicense,
                isDefault: false,
                isEnabled: isConsentTheSameWithCookie,
            })),
        }
        : subdomainPurposeLimitation;
});
const shouldSetNiCCookie = (newPurposeLimitations) => {
    const storedNiCCookies = Cookies.get(NI_C_COOKIES);
    if (!storedNiCCookies)
        return true;
    const cookiePurposeLimitationsValuesToSet = getCookiePurposeLimitationsValuesToSet(newPurposeLimitations);
    return storedNiCCookies !== cookiePurposeLimitationsValuesToSet;
};
const shouldSetSqCookieFor = (newPurposeLimitations) => {
    const storedSqCookies = Cookies.get(SQ_COOKIES);
    if (!storedSqCookies)
        return true;
    const sqCookieValueToSet = getSqCookieValueToSet(newPurposeLimitations);
    return storedSqCookies !== `${sqCookieValueToSet}`;
};
const checkIfServerLimitationLevelExceedLocal = (responseLimitations, storedPurposeLimitationsState) => {
    return Object.values(SERVER_PURPOSE_LIMITATION_NAMES).some((name) => {
        if (!name)
            return false;
        const storedLevel = storedPurposeLimitationsState.purposeLimitationsLevels[name];
        const responseLevel = responseLimitations.purposeLimitationsLevels[name];
        if (!storedLevel || !responseLevel)
            return false;
        const isResponseLevelEqualOrHigherPromptLimitLevel = responseLevel >= REQUIRED_PURPOSE_LIMITATION_PROMPT_LIMIT_LEVEL;
        const isResponseLevelHigherStored = responseLevel > storedLevel;
        return isResponseLevelEqualOrHigherPromptLimitLevel && isResponseLevelHigherStored;
    });
};
const handleLimitationsForSameUser = (responseLimitations, storedPurposeLimitationsState, guestLocation) => {
    const newPurposeLimitations = mergeOldGuestLimitationsWithNewVersion(storedPurposeLimitationsState.purposeLimitations, responseLimitations.purposeLimitations);
    if (shouldSetNiCCookie(newPurposeLimitations)) {
        setNiCCookie(newPurposeLimitations);
    }
    if (!checkIsSqCookieDeprecatedByLocation(guestLocation) &&
        shouldSetSqCookieFor(newPurposeLimitations)) {
        setSqCookie(newPurposeLimitations);
    }
    return {
        ...responseLimitations,
        purposeLimitations: newPurposeLimitations,
        lastUpdatedTimestamp: storedPurposeLimitationsState.lastUpdatedTimestamp,
    };
};
const handleMemberResponseServerLimitations = async ({ accessToken, userId, userExperience, memberLocation, noCache, }) => {
    const { purposeLimitations: purposeLimitationsServer } = await fetchPurposeLimitationsMember({
        accessToken,
        userExperience,
        memberLocation,
        noCache,
    });
    const responseLimitations = {
        userId,
        purposeLimitations: transformLimitations(purposeLimitationsServer),
        purposeLimitationsLevels: getLevelsFromLimitations(purposeLimitationsServer),
    };
    if (shouldSetNiCCookie(responseLimitations.purposeLimitations)) {
        setNiCCookie(responseLimitations.purposeLimitations);
    }
    if (!checkIsSqCookieDeprecatedByLocation(memberLocation) &&
        shouldSetSqCookieFor(responseLimitations.purposeLimitations)) {
        setSqCookie(responseLimitations.purposeLimitations);
    }
    return responseLimitations;
};
const handleLimitationsForSubDomain = (responseLimitations, oldNiCCookies) => ({
    ...responseLimitations,
    purposeLimitations: mergeSubdomainGuestLimitationsFromCookie(responseLimitations.purposeLimitations, oldNiCCookies),
});
const handleGuestResponseServerLimitations = async ({ userId, userExperience, guestLocation, storedPurposeLimitationsGuestState, }) => {
    const oldNiCCookies = Cookies.get(NI_C_COOKIES);
    const { purposeLimitations: purposeLimitationsServer, ...serverRest } = await fetchPurposeLimitationsGuest(userExperience, guestLocation);
    let responseLimitations = {
        userId,
        purposeLimitations: transformLimitations(purposeLimitationsServer),
        purposeLimitationsLevels: getLevelsFromLimitations(purposeLimitationsServer),
        lastUpdatedTimestamp: Date.now(),
        ...serverRest,
    };
    const canRetrievingLimitationGuestFromStore = checkRetrievingLimitationGuestFromStore();
    const serverLimitationLevelExceedLocal = checkIfServerLimitationLevelExceedLocal(responseLimitations, storedPurposeLimitationsGuestState);
    const currentUserIsStoredUser = storedPurposeLimitationsGuestState.userId === userId;
    const is13MonthRePromptNeeded = checkIs13MonthRePromptNeeded(storedPurposeLimitationsGuestState.lastUpdatedTimestamp, guestLocation);
    if (canRetrievingLimitationGuestFromStore &&
        !serverLimitationLevelExceedLocal &&
        currentUserIsStoredUser &&
        !is13MonthRePromptNeeded) {
        responseLimitations = handleLimitationsForSameUser(responseLimitations, storedPurposeLimitationsGuestState, guestLocation);
    }
    else {
        setNiCCookie(responseLimitations.purposeLimitations);
        if (!checkIsSqCookieDeprecatedByLocation(guestLocation)) {
            setSqCookie(responseLimitations.purposeLimitations);
        }
    }
    if (oldNiCCookies && checkIsSubDomain()) {
        responseLimitations = handleLimitationsForSubDomain(responseLimitations, oldNiCCookies);
    }
    return responseLimitations;
};
export { handleMemberResponseServerLimitations, handleGuestResponseServerLimitations };
